/**
 * Hook and utilities for handling deep linking functionality in the application.
 * Enables navigation to specific screens or performing actions based on incoming deep links.
 */
import { useEffect } from "react";
import { NativeModules, Platform } from "react-native";
import { useNavigation } from "@react-navigation/native";
import { getInitialURL, parse } from "expo-linking";
import { addEventListener as linkingAddEventListener } from "expo-linking";
import { nativeNavFromSlug } from "../components/Promotion/Utils";
import { fetchPost } from "../client";
import useRefreshUser from "./useRefreshUser";

/**
 * Retrieves a JWT token by logging in with a predefined test account.
 * Used for development and testing purposes.
 *
 * @param {('free'|'paid')} [type='paid'] - The account type to get a token for.
 * @returns {Promise<string>} A JWT token for the specified account type.
 */
const getToken = async (type: "free" | "paid" = "paid") => {
  const email = `${type}@onpacegroup.com`;
  const resp = await fetchPost(
    "member/authentication/login",
    {},
    { username: email, password: "abcDEF123!" }
  );

  return resp.jwt_token;
};

/**
 * A hook that handles deep linking functionality for the application.
 * It processes incoming deep links to navigate to specific screens or perform actions like login.
 * Only works on native platforms (not web).
 *
 * @returns {void}
 */
const useDeepLink = () => {
  const navigation = useNavigation();
  const { refreshUser } = useRefreshUser();

  useEffect(() => {
    if (Platform.OS === "web") return;

    /**
     * Processes a deep link and performs the appropriate action.
     * Can navigate to a specific screen or perform actions like user login.
     *
     * @param {Object} event - The deep link event.
     * @param {string} event.url - The URL from the deep link.
     * @returns {Promise<void>}
     */
    const handleDeepLink = async (event) => {
      console.log(`[DeepLink] Received Deep Link:: ${JSON.stringify(event)}`);
      let data = parse(event.url);
      if (data.path) {
        const { screen, paramsObj } = nativeNavFromSlug(data.path);
        navigation?.navigate(screen, paramsObj);
      }
      if (data?.queryParams?.action === "login") {
        const accountType = data?.queryParams?.accountType || "paid";
        const jwtToken = await getToken(accountType);
        refreshUser(jwtToken);
      }
    };

    // Check if the app was opened by a deep link
    getInitialURL().then((url) => {
      if (url) handleDeepLink({ url });
    });

    // Listen for incoming links
    const urlEventListener = linkingAddEventListener("url", handleDeepLink);

    // Cleanup the event listener
    return () => {
      urlEventListener.remove();
    };
  }, [navigation]);
};

export default useDeepLink;
